import React, {
	ReactNode,
	ReactNodeArray,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { uid } from 'react-uid';
import { fromJS } from 'immutable';

import Modal from '@/components/Modal/Modal';
import { searchAddSearchRequest } from '@/modules/search/searchActions';
import type * as SearchTypes from '@/modules/search/searchTypes';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import { useToast } from '@/components/atoms/Toast';
import Button from '@/components/Button/newButtons';
import { Tooltip } from '@/components/atoms/Tooltip';
import {
	dashboardAddBusinessToListRequest,
	dashboardAddListRequest,
	dashboardEditListRequest
} from '../../dashboardActions';
import type { Business, List } from '../../dashboardTypes';
import './ListModal.scss';

interface Props {
	businesses?: Business[];

	saveBusinessToList: (b?: Business[]) => void;

	removeListConfirm?: List;

	setRemoveListConfirm: (l?: List) => void;

	removeList: (l: List) => void;

	savedSearches?: SearchTypes.Search[];

	savedSearch?: SearchTypes.Search;

	setSavedSearch: (s?: SearchTypes.Search) => void;

	editList?: List;

	setEditList: (l?: List) => void;

	listChange: (list: string) => void;
	onListSelected?: (listId: string) => void; // Callback to return the listId
}

const ListModal = ({
	saveBusinessToList,
	businesses,
	removeListConfirm,
	setRemoveListConfirm,
	removeList,
	savedSearches,
	savedSearch,
	setSavedSearch,
	editList,
	setEditList,
	listChange,
	onListSelected
}: Props): JSX.Element => {
	const [isMounted, setIsMounted] = useState(false);
	useEffect(() => {
		setIsMounted(true);
	}, []);

	const [
		modalBusiness,
		user,
		loading,
		addBusinessToListSuccess,
		currentList,
		allLists
	] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn(['businesses', 'modalBusiness']),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.dashboard.getIn(['lists', 'new', 'loading']),
			state.dashboard.getIn(['lists', 'new', 'success']),
			state.dashboard.getIn(['lists', 'selected', 'id']),
			state.dashboard.getIn(['lists', 'data']).toJS()
		],
		shallowEqual
	);

	const listsOrderedByUpdatedAt = useMemo(() => {
		return [...allLists].sort(
			(a: List, b: List) =>
				new Date(b.updatedAt).getTime() -
				new Date(a.updatedAt).getTime()
		);
	}, [allLists]);
	const [listToNavigateTo, setListToNavigateTo] = useState<string | null>(
		null
	);
	const { toast } = useToast();
	const [isNew, setIsNew] = useState(false);
	const [displaySameListNameWarning, setDisplaySameListNameWarning] =
		useState(false);
	const dispatch = useDispatch();
	const addList = (name: string, businessIds: string[]) =>
		dispatch(dashboardAddListRequest({ name, businessIds } as List));
	const updateList = (listId: string | undefined, name: string) =>
		dispatch(dashboardEditListRequest({ listId, name } as List));
	const addBusinessToList = (businessIds: string[], listId: string) => {
		const businessNames = [];
		// eslint-disable-next-line no-plusplus
		for (let i = 0; i < businesses.length; i++) {
			businessNames.push(businesses[i].name);
		}
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User added businesses to a list',
				properties: {
					businessNames,
					listId
				}
			});
		}
		onListSelected?.(listId);
		dispatch(dashboardAddBusinessToListRequest({ businessIds, listId }));
	};
	const addSearch = (s: SearchTypes.Search) =>
		dispatch(searchAddSearchRequest(s));

	const modalInputRef = useRef<HTMLInputElement>();

	const title = useRef<ReactNode>(null);
	const buttons = useRef<ReactNodeArray>([]);
	const body = useRef<ReactNode>();

	useEffect(() => {
		if (isNew) {
			setIsNew(false);
		}

		if (savedSearch) {
			setSavedSearch(undefined);
		}
	}, [
		allLists.length,
		savedSearches?.length,
		JSON.stringify(savedSearches?.map(search => search.name))
	]);

	useEffect(() => {
		if (!addBusinessToListSuccess || !listToNavigateTo) return;

		const createdList = allLists.find(
			(list: List) => list.id === listToNavigateTo
		);

		if (createdList?.id) {
			listChange(createdList.id);
			setListToNavigateTo(null);
		} else {
			listChange(allLists[0]?.id);
		}
	}, [addBusinessToListSuccess, listToNavigateTo, allLists, listChange]);

	const saveList = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'NEW ANALYTIC Save a new list'
			});
		}

		if (modalInputRef.current && modalInputRef.current?.value.length > 0) {
			const trimmedName = modalInputRef.current?.value.trim();

			if (trimmedName) {
				const businessIds = modalBusiness
					? [modalBusiness.businessId]
					: (businesses?.map(
							(business: Business) =>
								business._id ||
								business.id ||
								business.businessId
						) as string[]);

				addList(modalInputRef.current?.value, businessIds);
				setListToNavigateTo(trimmedName);
			} else {
				toast({
					title: 'List name cannot be empty',
					variant: 'error'
				});
			}
		}
		if (user && user.email) {
			const businessIds = [];
			if (businesses) {
				for (let i = 0; i < businesses.length; i++) {
					businessIds.push(businesses[i]._id);
				}
			}
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User created a list',
				properties: {
					businessIds:
						businessIds.length < 1000
							? businessIds
							: 'List too large to display Ids',
					numBusinesses: businesses?.length,
					listName: modalInputRef.current?.value
				}
			});
		}

		const createdList = allLists.find(
			list => list.name === modalInputRef.current?.value
		);

		if (createdList) {
			onListSelected?.(createdList.id); // Send the listId back
		}
	};

	const listNameExists = () => {
		const listExists = allLists.some(
			(list: List) =>
				list.name?.toLowerCase() ===
				modalInputRef.current?.value.toLowerCase()
		);

		setDisplaySameListNameWarning(listExists);

		return listExists;
	};

	const updateListData = () => {
		if (modalInputRef.current && modalInputRef.current?.value.length > 0) {
			updateList(editList?.id, modalInputRef.current?.value);
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User edited a list name',
					properties: {
						listInfo: editList,
						previousName: editList?.name,
						newName: modalInputRef.current?.value
					}
				});
			}
		}
	};

	const saveSearch = () => {
		if (modalInputRef.current && modalInputRef.current?.value.length > 0) {
			const savedSearchNew = fromJS(savedSearch).toJS();

			savedSearchNew.name = modalInputRef.current?.value;

			addSearch(savedSearchNew);

			setSavedSearch(savedSearchNew);

			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User saved a search',
					properties: {
						filters: savedSearchNew?.filters,
						parameters: savedSearchNew?.params
					}
				});
			}
		}
	};

	const saveButtonClickHandler = () => {
		if (savedSearch) {
			saveSearch();
		} else if (editList) {
			updateListData();
		} else if (!loading && !listNameExists()) {
			saveList();
		}
	};

	const modalClose = () => {
		setDisplaySameListNameWarning(false);

		if (isNew) {
			setIsNew(false);
		}

		if (businesses) {
			saveBusinessToList(undefined);
		}

		if (removeListConfirm) {
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User deleted a list',
					properties: {
						listInfo: removeListConfirm,
						listSize: removeListConfirm.businessIds?.length,
						listName: removeListConfirm.name,
						listId: removeListConfirm.id
					}
				});
			}
			setRemoveListConfirm(undefined);
		}

		if (savedSearch) {
			setSavedSearch(undefined);
		}

		if (editList) {
			setEditList(undefined);
		}
	};

	useEffect(() => {
		if (isMounted) {
			setTimeout(modalClose, 500);
		}
	}, [JSON.stringify(allLists)]);

	if (savedSearch) {
		title.current = 'Save filters';
	} else if (businesses && !removeListConfirm) {
		title.current = 'Save to a List';
	} else if (editList) {
		title.current = 'Edit List';
	} else if (removeListConfirm) {
		title.current = 'Delete list?';
	}

	if (removeListConfirm) {
		body.current = null;
	} else if (isNew || savedSearch || editList) {
		body.current = (
			<form
				className="current-body flex flex-col pt-4"
				onSubmit={e => {
					e.preventDefault();

					if (savedSearch) {
						saveSearch();
					} else if (editList) {
						updateListData();
					} else {
						saveList();
					}
				}}
			>
				<p className="listModal-title font-semibold">Name</p>
				<input
					ref={el => {
						el?.focus();
						(modalInputRef.current as ReactNode) = el;
					}}
					className="mt-2 flex w-full"
					id="save-to-list-input-name"
					defaultValue={editList?.name}
				/>
			</form>
		);
	} else if (businesses) {
		body.current = (
			<div className="pt-4">
				<button
					type="button"
					className="primary listModal-button rounded px-2 font-semibold"
					id="create-new-list-button"
					onClick={() => {
						setIsNew(true);
					}}
				>
					Create a New List
				</button>
				<div className="listModal-list mb-2 mt-6 flex flex-col overflow-y-auto">
					{listsOrderedByUpdatedAt.map((list, index) => {
						if (list.id === currentList) return null;

						return (
							<div
								key={uid(list, index)}
								className={`py-4 ${
									index > 0 ? 'border-t-0' : ''
								}listModal-list--item`}
							>
								<div className="flex items-center justify-between gap-2 px-2">
									<div className="flex flex-col overflow-hidden">
										<Tooltip.Provider>
											<Tooltip.Root>
												<Tooltip.Trigger className="overflow-hidden text-ellipsis whitespace-nowrap font-medium">
													{list.name}
												</Tooltip.Trigger>
												<Tooltip.Content>
													{list.name}
												</Tooltip.Content>
											</Tooltip.Root>
										</Tooltip.Provider>
										<p>
											{list?.businessIds?.length ?? 0}{' '}
											items
										</p>
									</div>

									<Tooltip.Provider>
										<Tooltip.Root>
											<Tooltip.Trigger asChild>
												<Button
													disabled={
														list?.businessIds
															.length +
															businesses.length >
														15000
													}
													type="button"
													variant="primary"
													onClick={() => {
														addBusinessToList(
															businesses.map(
																(b: Business) =>
																	b._id
															),
															list?.id as string
														);
														setListToNavigateTo(
															list.id
														);
													}}
												>
													<span className="pr-1">
														+
													</span>
													<span>Save to List</span>
												</Button>
											</Tooltip.Trigger>

											{/* Show tooltip only if disabled */}
											{list?.businessIds.length +
												businesses.length >
												15000 && (
												<Tooltip.Content className="z-[1100] h-16 w-52 border border-dm-white bg-dm-blue text-dm-white">
													Lists can contain up to
													15,000 businesses. Create a
													new one to save more
													businesses.
												</Tooltip.Content>
											)}
										</Tooltip.Root>
									</Tooltip.Provider>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	if (isNew || savedSearch || editList) {
		buttons.current = [
			<button
				key={0}
				className="secondary ml-8 rounded px-4 py-2"
				type="button"
				onClick={modalClose}
			>
				Cancel
			</button>,
			<button
				key={1}
				className="primary ml-3 rounded px-4 py-2"
				type="button"
				onClick={saveButtonClickHandler}
			>
				{displaySameListNameWarning && (
					<span className="save-to-list-warning-text">
						A list with this name already exists. Please try a
						different name.
					</span>
				)}
				Save
			</button>
		];
	} else if (!businesses && removeListConfirm) {
		buttons.current = [
			<button
				key={0}
				className="secondary ml-8 rounded px-4 py-2"
				type="button"
				onClick={modalClose}
			>
				Cancel
			</button>,
			<button
				key={1}
				className="primary ml-3 rounded px-4 py-2"
				type="button"
				onClick={() => removeList(removeListConfirm)}
			>
				Confirm
			</button>
		];
	} else if (businesses) {
		buttons.current = [];
	}

	return (
		<Modal
			bodyClass="save-search-holder"
			onClose={modalClose}
			title={title.current}
			content={body.current}
			buttons={buttons.current}
			overlayClose
			show={
				!!(
					businesses ||
					removeListConfirm ||
					(savedSearch && !savedSearch.name) ||
					editList
				)
			}
		/>
	);
};

export default ListModal;
